import React, { useContext, useState, useEffect, useRef, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import es from "date-fns/locale/es";
import { BsCalendar } from "react-icons/bs";
import { RoomContext } from "../../context/RoomContext";
import "./style.css";

const CustomInput = forwardRef(({ onClick, checkInDate, checkOutDate }, ref) => {
  const formatDate = (date) => {
    if (!date) return "";
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  let displayText = "";
  if (checkInDate && checkOutDate) {
    displayText = `${formatDate(checkInDate)}  →  ${formatDate(checkOutDate)}`;
  } else if (checkInDate) {
    displayText = `${formatDate(checkInDate)}  →  Check-out`;
  }

  return (
    <div className="date-range-input" onClick={onClick} ref={ref}>
      <BsCalendar className="text-accent text-base flex-shrink-0" />
      <span className={displayText ? "date-range-text" : "date-range-placeholder"}>
        {displayText || "Check-in  →  Check-out"}
      </span>
    </div>
  );
});

CustomInput.displayName = "CustomInput";

const DateRangePicker = () => {
  const { checkInDate, setCheckInDate, setCheckOutDate, checkOutDate, blockedDates, bookingWindowDays } =
    useContext(RoomContext);

  const datepickerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxDate = React.useMemo(() => {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + (bookingWindowDays || 45));
    return futureDate;
  }, [bookingWindowDays]);

  const normalizeDate = (date) => {
    if (!date) return null;
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  // filterDate de react-datepicker: true = el día se puede elegir
  const isDateBlocked = (date) => {
    if (!blockedDates || blockedDates.length === 0) return true;

    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    const blocked = blockedDates.find((bd) => bd.date.split("T")[0] === dateStr);
    if (!blocked) return true;

    // Día bloqueado que permite check-out: se puede elegir solo como fecha de salida,
    // es decir, cuando ya hay una entrada elegida y este día es posterior a ella.
    if (blocked.allowCheckout && checkInDate && !checkOutDate) {
      return normalizeDate(date).getTime() > normalizeDate(checkInDate).getTime();
    }

    return false;
  };

  const handleRangeChange = (dates) => {
    const [start, end] = dates;

    if (start && end) {
      const normalizedStart = normalizeDate(start);
      let normalizedEnd = normalizeDate(end);

      // Enforce minimum 1 night
      if (normalizedEnd.getTime() <= normalizedStart.getTime()) {
        normalizedEnd = new Date(normalizedStart);
        normalizedEnd.setDate(normalizedStart.getDate() + 1);
        normalizedEnd = normalizeDate(normalizedEnd);
      }

      setCheckInDate(normalizedStart);
      setCheckOutDate(normalizedEnd);

      // Close calendar after range is complete
      setTimeout(() => {
        datepickerRef.current?.setOpen(false);
      }, 150);
    } else {
      // First click: set check-in, clear check-out
      setCheckInDate(normalizeDate(start));
      setCheckOutDate(null);
    }
  };

  return (
    <div className="w-full h-full date-range-wrapper">
      <DatePicker
        ref={datepickerRef}
        selectsRange
        startDate={checkInDate}
        endDate={checkOutDate}
        onChange={handleRangeChange}
        monthsShown={isMobile ? 1 : 2}
        minDate={normalizeDate(new Date())}
        maxDate={maxDate}
        filterDate={isDateBlocked}
        dateFormat="dd/MM/yyyy"
        locale={es}
        shouldCloseOnSelect={false}
        withPortal={isMobile}
        // En mobile el calendario va al <body>: si se renderiza dentro de la hoja del buscador
        // (que tiene transform por su animación), el "fixed" del portal queda atrapado en la hoja.
        portalId={isMobile ? "datepicker-portal" : undefined}
        popperPlacement="bottom-start"
        customInput={
          <CustomInput checkInDate={checkInDate} checkOutDate={checkOutDate} />
        }
      />
    </div>
  );
};

export default DateRangePicker;
